import React, { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "ncoded-component-library";
import api from "api";
import {
  REPORT_STATUS,
  ReportStatus,
  ReportUserEntity,
} from "models/Moderation";
import SimplePagination from "./SimplePagination";

const PAGE_SIZE = 10;
const QUERY_KEY = "admin-user-reports";

const formatUser = (u?: { id: number; firstName?: string; lastName?: string }) =>
  !u ? "-" : `#${u.id} ${u.firstName || ""} ${u.lastName || ""}`.trim();

const UserReportsTable = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const queryClient = useQueryClient();

  const fetchReports = useCallback(async () => {
    const { data } = await api.moderation.getUserReports({
      $take: PAGE_SIZE,
      $page: pageIndex + 1,
      $order: "-id",
      $relations: "reporter,reported",
    });
    return data;
  }, [pageIndex]);

  const query = useQuery({
    queryKey: [QUERY_KEY, pageIndex],
    queryFn: fetchReports,
    keepPreviousData: true,
  });

  const mutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ReportStatus }) =>
      api.moderation.updateUserReport(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  const rows: ReportUserEntity[] = query.data?.items || [];
  const pageCount = query.data?.totalPages || 0;

  return (
    <div>
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Type</th>
            <th>Status</th>
            <th>Reporter</th>
            <th>Reported</th>
            <th>Info</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && !query.isFetching ? (
            <tr>
              <td colSpan={8}>No reports.</td>
            </tr>
          ) : null}
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.id}</td>
              <td>{r.type}</td>
              <td>{r.status || "-"}</td>
              <td>{formatUser(r.reporter)}</td>
              <td>{formatUser(r.reported)}</td>
              <td>{r.info || "-"}</td>
              <td>{new Date(r.createdAt).toLocaleString()}</td>
              <td>
                <div className="anys-moderation-page__row-actions">
                  <Button
                    variant="outline"
                    onClick={() =>
                      mutation.mutate({
                        id: r.id,
                        status: REPORT_STATUS.IN_REVIEW,
                      })
                    }
                    disabled={mutation.isLoading}
                  >
                    In review
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      mutation.mutate({
                        id: r.id,
                        status: REPORT_STATUS.NORMAL,
                      })
                    }
                    disabled={mutation.isLoading}
                  >
                    Normal
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      mutation.mutate({
                        id: r.id,
                        status: REPORT_STATUS.RESTRICTED,
                      })
                    }
                    disabled={mutation.isLoading}
                  >
                    Restrict
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <SimplePagination
        pageIndex={pageIndex}
        pageCount={pageCount}
        onPrev={() => setPageIndex((i) => Math.max(0, i - 1))}
        onNext={() => setPageIndex((i) => i + 1)}
        isFetching={query.isFetching}
      />
    </div>
  );
};

export default UserReportsTable;
